import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { getUserProfile, updateUserProfile, updateUserProfileImage } from '../../../store/slices/authSlice';
import utilityService from '../../../services/utilityService';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import TwoFactorAuthModal from '../../../components/TwoFactorAuthModal/TwoFactorAuthModal';
import ChangePasswordModal from '../../../components/ChangePasswordModal/ChangePasswordModal';
import { useProfile } from '../../../jetSetter/hooks';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';

const AccountSettings = () => {
    const dispatch = useAppDispatch();
    const { user, profileLoading } = useAppSelector((state) => state.auth);
    const { twoFactorEnabled, enableTwoFactor, disableTwoFactor, loadProfile } = useProfile();

    // 2FA Modal States
    const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
    const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Notification Settings State
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);

    // Change Password Modal States
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    // Avatar ref
    const fileInputRef = useRef(null);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [profileImagePreview, setProfileImagePreview] = useState('');

    // Form states
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        country_id: '',
        mobile_number: '',
        timezone: '',
        address: '',
        bankName: '',
        accountNumber: '',
        accountHolder: '',
        routingNumber: ''
    });

    const [countries, setCountries] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingTimezones, setLoadingTimezones] = useState(false);

    useEffect(() => {
        dispatch(getUserProfile());
        fetchCountries();
        fetchTimezones();
    }, [dispatch]);

    const fetchCountries = async () => {
        try {
            setLoadingCountries(true);
            const response = await utilityService.getCountries();
            if (response.success) {
                setCountries(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch countries:', err);
        } finally {
            setLoadingCountries(false);
        }
    };

    const fetchTimezones = async () => {
        try {
            setLoadingTimezones(true);
            const response = await utilityService.getTimezones();
            if (response.success) {
                setTimezones(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch timezones:', err);
        } finally {
            setLoadingTimezones(false);
        }
    };

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                mobile_number: user.mobile_number || '',
                country_id: user.country_id || '',
                timezone: user.timezone || '',
                address: user.address || '',
                bankName: user.bank_name || '',
                accountNumber: user.account_number || '',
                accountHolder: user.account_holder || '',
                routingNumber: user.routing_number || ''
            }));
            setNotificationsEnabled(user.notifications_enabled ?? true);
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Build payload with all fields including bank details
            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                mobile_number: formData.mobile_number,
                country_id: formData.country_id !== "" ? Number(formData.country_id) : null,
                timezone: formData.timezone || null,
                address: formData.address || null,
                // Bank details
                bank_name: formData.bankName || null,
                account_number: formData.accountNumber || null,
                account_holder: formData.accountHolder || null,
                routing_number: formData.routingNumber || null
            };
            
            await dispatch(updateUserProfile(payload)).unwrap();
            toast.success("Profile updated successfully!");
        } catch (error) {
            toast.error(error || "Failed to update profile.");
        } finally {
            setIsSaving(false);
        }
    };

    // 2FA Handlers
    const handleToggle2FA = async (e) => {
        const isEnabled = e.target.checked;

        if (isEnabled) {
            setShowTwoFactorModal(true);
        } else {
            setShowDisable2FAModal(true);
        }
    };

    const handleConfirmDisable2FA = async () => {
        try {
            await disableTwoFactor();
            toast.success('2FA disabled successfully');
            setShowDisable2FAModal(false);
            await loadProfile();
        } catch (error) {
            toast.error(error?.message || 'Failed to disable 2FA');
            setShowDisable2FAModal(false);
        }
    };

    const handleCancelDisable2FA = () => {
        setShowDisable2FAModal(false);
    };

    const handleTwoFactorSave = async (authData) => {
        try {
            await enableTwoFactor({
                method: authData.method,
                security_questions: authData.security_questions
            });
            toast.success('2FA enabled successfully');
            setShowTwoFactorModal(false);
            await loadProfile();
        } catch (error) {
            toast.error(error?.message || 'Failed to enable 2FA');
        }
    };

    const handleTwoFactorClose = () => {
        setShowTwoFactorModal(false);
    };

    // Notification Settings Handler
    const handleToggleNotifications = async (e) => {
        const isEnabled = e.target.checked;
        setNotificationsEnabled(isEnabled);
        
        try {
            const response = await api.post('/auth/notification-settings', {
                notifications_enabled: isEnabled ? 1 : 0
            });

            if (response.success) {
                toast.success(`Notifications ${isEnabled ? 'enabled' : 'disabled'} successfully`);
            } else {
                toast.error(response.message || 'Failed to update notification settings');
                setNotificationsEnabled(!isEnabled);
            }
        } catch (error) {
            console.error('Failed to update notification settings:', error);
            toast.error('Failed to update notification settings');
            setNotificationsEnabled(!isEnabled);
        }
    };

    // Avatar Upload Handler
    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setAvatarLoading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const base64Image = event.target.result;
                    setProfileImagePreview(base64Image);
                    await dispatch(updateUserProfileImage(base64Image)).unwrap();
                    toast.success('Profile image updated successfully');
                    await dispatch(getUserProfile()).unwrap();
                } catch (err) {
                    toast.error(err || 'Failed to update image.');
                    setProfileImagePreview('');
                } finally {
                    setAvatarLoading(false);
                }
            };
            reader.onerror = () => {
                toast.error('Failed to read file');
                setAvatarLoading(false);
            };
            reader.readAsDataURL(file);
        } catch (err) {
            toast.error(err.message || 'Failed to update image.');
            setAvatarLoading(false);
        }
    };

    if (profileLoading && !user) {
        return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }

    return (
        <div className="min-h-screen p-6">
            {/* Page Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-normal text-gray-800">Account Settings</h1>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Left Sidebar */}
                <div className="w-full lg:w-[280px] flex flex-col gap-4">
                    {/* Profile Card */}
                    <div className="bg-white rounded-sm shadow-sm">
                        <div className="p-6 flex flex-col items-center">
                            <div className="relative mb-4 group cursor-pointer" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                {/* Profile Image with Double Border (Gray outer, White inner) */}
                                <div className="w-28 h-28 rounded-full border-[3px] border-gray-300 p-[3px] bg-white shadow-md">
                                    <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 flex items-center justify-center relative">
                                        {avatarLoading ? (
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                                        ) : (
                                            <img
                                                key={profileImagePreview || user?.image_url}
                                                src={profileImagePreview || user?.image_url || '/images/default-avatar.png'}
                                                alt="Profile"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23cbd5e1"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                {/* Hover Tooltip */}
                                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                    Click to change profile image.
                                    <div className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
                                </div>
                                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageChange} />
                            </div>

                            {/* Name - Split into 3 lines */}
                            <h3 className="text-lg font-semibold text-gray-800 text-center mb-0 leading-tight">
                                {formData.first_name || 'JUNN'}
                            </h3>
                            <h3 className="text-lg font-semibold text-gray-800 text-center mb-0 leading-tight">
                                {formData.last_name ? formData.last_name.split(' ')[0] : 'YEOW'}
                            </h3>
                            <h3 className="text-lg font-semibold text-gray-800 text-center mb-1 leading-tight">
                                {formData.last_name ? formData.last_name.split(' ').slice(1).join(' ') : 'CHEE'}
                            </h3>
                            <p className="text-gray-500 text-xs mb-4 capitalize">{user?.user_type || 'Admin'}</p>

                            <button
                                onClick={() => setShowPasswordModal(true)}
                                className="w-full bg-[#ffd000] hover:bg-[#e6bd00] text-black font-semibold py-2.5 px-4 rounded-sm transition-colors text-sm"
                            >
                                Change Password
                            </button>
                        </div>
                    </div>

                    {/* 2FA Authentication Card */}
                    <div className="bg-white rounded-sm shadow-sm p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-700 text-sm font-normal">2FA Authentication</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={twoFactorEnabled}
                                    onChange={handleToggle2FA}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffd000]"></div>
                            </label>
                        </div>
                    </div>

                    {/* Notification Settings Card */}
                    <div className="bg-white rounded-sm shadow-sm p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-700 text-sm font-normal">Notification Settings</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notificationsEnabled}
                                    onChange={handleToggleNotifications}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffd000]"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Right Content Area */}
                <div className="flex-1">
                    {/* Personal Details Section */}
                    <div className="bg-white rounded-sm shadow-sm mb-4">
                        <div className="border-b border-gray-200 px-4 py-3">
                            <h3 className="text-base font-semibold text-gray-800">Personal Details</h3>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="First Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="Last Name"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        disabled
                                        className="w-full border border-gray-300 bg-gray-50 cursor-not-allowed rounded-sm px-3 py-2 text-sm text-gray-600"
                                        placeholder="Email"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex-[3]">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Country Code <span className="text-red-500">*</span>
                                        </label>
                                        <SearchableSelect
                                            name="country_id"
                                            value={formData.country_id}
                                            onChange={handleInputChange}
                                            options={countries.map(country => ({
                                                value: country.id,
                                                label: `${country.name} ${country.phone_code}`
                                            }))}
                                            placeholder="Select Country Code"
                                            disabled={loadingCountries}
                                        />
                                    </div>
                                    <div className="flex-[2]">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Mobile Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="mobile_number"
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-400"
                                            placeholder="Mobile Number"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                                    <SearchableSelect
                                        name="timezone"
                                        value={formData.timezone}
                                        onChange={handleInputChange}
                                        options={timezones.map(tz => ({
                                            value: tz.id,
                                            label: `${tz.location} (${tz.timezone})`
                                        }))}
                                        placeholder="Select Timezone"
                                        disabled={loadingTimezones}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-400 uppercase resize-y"
                                    placeholder="Address"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Bank Details Section */}
                    <div className="bg-white rounded-sm shadow-sm mb-4">
                        <div className="border-b border-gray-200 px-4 py-3">
                            <h3 className="text-base font-semibold text-gray-800">Bank Details</h3>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                                    <input
                                        type="text"
                                        name="bankName"
                                        value={formData.bankName}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="Bank Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                                    <input
                                        type="text"
                                        name="accountNumber"
                                        value={formData.accountNumber}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="Account Number"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Holder</label>
                                    <input
                                        type="text"
                                        name="accountHolder"
                                        value={formData.accountHolder}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="Account Holder"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Routing Number</label>
                                    <input
                                        type="text"
                                        name="routingNumber"
                                        value={formData.routingNumber}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase text-gray-700 focus:outline-none focus:border-blue-400"
                                        placeholder="Routing Number"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white rounded-sm shadow-sm p-4">
                        <div className="flex justify-end gap-2">
                            <button
                                className="bg-[#ffd000] hover:bg-[#e6bd00] text-black font-semibold px-6 py-2 rounded-sm text-sm transition-colors disabled:opacity-50"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className="bg-white hover:bg-gray-50 text-gray-700 font-semibold px-6 py-2 rounded-sm text-sm transition-colors border border-gray-300"
                                onClick={() => window.location.reload()}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <UniversalConfirmModal
                isOpen={showDisable2FAModal}
                onClose={handleCancelDisable2FA}
                onConfirm={handleConfirmDisable2FA}
                type="warning"
                title="Disable 2FA?"
                message="Are you sure you want to disable two-factor authentication? This will make your account less secure."
                confirmText="YES, DISABLE"
                cancelText="CANCEL"
                theme="tailwind"
            />

            <TwoFactorAuthModal
                isOpen={showTwoFactorModal}
                onClose={handleTwoFactorClose}
                onSave={handleTwoFactorSave}
            />

            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                mode="self"
            />
        </div>
    );
};

export default AccountSettings;
